/**
 * NOTIFICATION TOAST
 * 
 * Displays toast notifications for foreground messages
 * Appears in corner of screen, clickable to navigate
 * 
 * Location: src/components/NotificationToast.tsx
 */

import React from 'react';
import { X, MessageCircle, Heart, ShoppingBag, UserPlus, Bell } from 'lucide-react';
import { useForegroundNotifications } from '../hooks/useForegroundNotifications';

/**
 * Get icon for notification type
 */
const getNotificationIcon = (type?: string) => {
  switch (type) {
    case 'message':
      return <MessageCircle className="icon-md text-blue-500" />;
    case 'wishlist_available':
      return <Heart className="icon-md text-pink-500" />;
    case 'destash_inquiry':
      return <ShoppingBag className="icon-md text-green-500" />;
    case 'new_follower':
      return <UserPlus className="icon-md text-purple-500" />;
    default:
      return <Bell className="icon-md text-glass" />;
  }
};

/**
 * Container that renders all active notification toasts
 */
export const NotificationToastContainer: React.FC = () => {
  const { toasts, dismissToast, handleToastClick } = useForegroundNotifications();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          onClick={() => handleToastClick(toast)}
          className="glass-modal border border-slate-600 p-4 cursor-pointer hover:shadow-xl transition-all transform hover:scale-[1.02] animate-slide-in font-body"
        >
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="flex-shrink-0 mt-0.5">
              {getNotificationIcon(toast.type)}
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-glass text-sm">
                {toast.title}
              </p>
              {toast.body && (
                <p className="text-glass-secondary text-sm mt-0.5 line-clamp-2">
                  {toast.body}
                </p>
              )}
            </div>
            
            {/* Close button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                dismissToast(toast.id);
              }}
              className="flex-shrink-0 text-glass-muted hover:text-glass-secondary transition-default"
              aria-label="Dismiss"
            >
              <X className="icon-sm" />
            </button>
          </div>
          
          {/* Click hint */}
          <p className="text-xs text-glass-muted mt-2">
            Click to view
          </p>
        </div>
      ))}
    </div>
  );
};

/**
 * CSS for slide-in animation (add to your global CSS or tailwind config)
 * 
 * @keyframes slide-in {
 *   from {
 *     transform: translateX(100%);
 *     opacity: 0;
 *   }
 *   to {
 *     transform: translateX(0);
 *     opacity: 1;
 *   }
 * }
 * 
 * .animate-slide-in {
 *   animation: slide-in 0.3s ease-out;
 * }
 */

export default NotificationToastContainer;
